import { onTagReorder } from '@griply/utils';
import { useUpdateTasks } from 'features/tasks';
import React, { useMemo } from 'react';
import {
  rootId,
  TaskListItemTypes,
  TreeItem,
  useTree,
  VirtualizedTreeList,
} from 'shared/components/ui/virtualized-tree-list';
import { useToday } from 'shared/contexts/today';
import {
  useRelationships,
  useRelationshipsMap,
} from 'shared/hooks/use-relationships';
import { useTaskDateShortcuts } from 'shared/hooks/use-task-date-shortcuts';
import { useTaskPriorityShortcuts } from 'shared/hooks/use-task-priority-shortcuts';
import { ID } from 'shared/types/id';
import { Task } from 'shared/types/task';

import { useTagTree } from '../../hooks/use-tag-tree';
import { GroupedListProps } from '../../types';
import * as Styled from './tag-grouping-tasks-list.style';
import { TagRenderData, TagRenderDataContext } from './tag-render-context';
import { TagRenderItem } from './tag-render-item';

const taskItemTypes = [TaskListItemTypes.Task];

export const TagGroupingTasksList: React.FC<GroupedListProps> = ({
  tasks,
  tags,
  sortingMode,
  onReorder,
  customOrder = {},
  collapsedSections = [],
  onSectionCollapse,
  weekStartsOn,
  scrollElement,
  denseFormat,
  groupSettings,
  isContextMenuOpen,
  defaultNewTaskDate,
}) => {
  const today = useToday();
  const relationships = useRelationships();
  const relationshipsMap = useRelationshipsMap(relationships);

  const tasksMap = useMemo(
    () =>
      tasks.reduce<Record<ID, Task>>((acc, task) => {
        acc[task.id] = task;
        return acc;
      }, {}),
    [tasks],
  );

  const treeData = useTagTree({
    tasks,
    tags,
    relationships,
    customOrder,
    sortingMode,
    today,
    weekStartsOn,
    showEmptyGroups: groupSettings.showEmptyGroups,
    hideNewTaskRow: groupSettings.hideNewTaskRow,
  });

  const oldTreeData = useMemo(() => structuredClone(treeData), [treeData]);

  const _onReorder = (updatedItems: TreeItem[]) => {
    if (!onReorder) {
      return;
    }

    const result = onTagReorder({
      updatedItems,
      oldItems: Object.values(oldTreeData),
      tasksMap,
      relationshipsMap,
    });

    onReorder(
      result.updatedSections,
      result.updatedTasks,
      result.relationshipUpdates,
    );
  };

  const renderData = useMemo<TagRenderData>(
    () => ({
      groupSettings,
      tasksMap,
      tasks,
      tags,
      today,
      denseFormat,
      defaultNewTaskDate,
    }),
    [
      groupSettings,
      tasksMap,
      tasks,
      tags,
      today,
      denseFormat,
      defaultNewTaskDate,
    ],
  );

  const [tree] = useTree();
  const { submit: updateTasks } = useUpdateTasks();

  useTaskPriorityShortcuts(tree, tasks, updateTasks);
  useTaskDateShortcuts(tree, tasks, updateTasks);

  return (
    <Styled.Container>
      <TagRenderDataContext.Provider value={renderData}>
        <VirtualizedTreeList
          rootItemId={rootId}
          items={treeData}
          renderItem={TagRenderItem}
          scrollElement={scrollElement}
          multiselectTypes={taskItemTypes}
          dragTypes={taskItemTypes}
          onReorder={_onReorder}
          collapsedItems={collapsedSections}
          onCollapse={onSectionCollapse}
          denseFormat={denseFormat}
          isContextMenuOpen={isContextMenuOpen}
        />
      </TagRenderDataContext.Provider>
    </Styled.Container>
  );
};
